/**
 * What a person has typed or opened, carried across a redraw.
 *
 * A live update redraws the screen from the store, which would otherwise throw
 * away a half-written reason, an amount, or the payment someone opened to
 * review. Before a redraw the app captures every field that differs from how the page
 * drew it, and every open panel; afterwards it puts them back on the elements
 * with the same key. A field is keyed by its `id`, or by `data-draft` when it
 * has none. A panel is any element with `data-panel`, open when it lacks the
 * `hidden` class.
 */

type Field = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export interface Drafts {
  values: Map<string, string>;
  checked: Map<string, boolean>;
  open: Set<string>;
  caret: { key: string; start: number; end: number } | null;
}

function keyOf(element: Element): string | null {
  return element.id || element.getAttribute("data-draft") || null;
}

function drewAs(field: Field): string {
  if (field instanceof HTMLSelectElement) {
    const chosen = [...field.options].find((option) => option.defaultSelected) ?? field.options[0];
    return chosen?.value ?? "";
  }
  return field.defaultValue;
}

export function captureDrafts(root: ParentNode, active: Element | null): Drafts {
  const drafts: Drafts = { values: new Map(), checked: new Map(), open: new Set(), caret: null };
  for (const field of root.querySelectorAll<Field>("input, textarea, select")) {
    const key = keyOf(field);
    if (!key || (field instanceof HTMLInputElement && field.type === "file")) {
      continue;
    }
    if (
      field instanceof HTMLInputElement &&
      (field.type === "checkbox" || field.type === "radio")
    ) {
      if (field.checked !== field.defaultChecked) {
        drafts.checked.set(key, field.checked);
      }
    } else if (field.value !== drewAs(field)) {
      drafts.values.set(key, field.value);
    }
    if (
      field === active &&
      (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) &&
      field.selectionStart !== null &&
      field.selectionEnd !== null
    ) {
      drafts.caret = { key, start: field.selectionStart, end: field.selectionEnd };
    }
  }
  for (const panel of root.querySelectorAll<HTMLElement>("[data-panel]")) {
    if (!panel.classList.contains("hidden")) {
      drafts.open.add(panel.dataset.panel ?? "");
    }
  }
  return drafts;
}

export function restoreDrafts(root: ParentNode, drafts: Drafts): void {
  for (const field of root.querySelectorAll<Field>("input, textarea, select")) {
    const key = keyOf(field);
    if (!key) {
      continue;
    }
    const value = drafts.values.get(key);
    if (value !== undefined) {
      field.value = value;
    }
    const checked = drafts.checked.get(key);
    if (checked !== undefined && field instanceof HTMLInputElement) {
      field.checked = checked;
    }
    if (
      drafts.caret?.key === key &&
      (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)
    ) {
      try {
        field.setSelectionRange(drafts.caret.start, drafts.caret.end);
      } catch {
        // Number and date inputs have no caret to put back; the value is enough.
      }
    }
  }
  for (const panel of root.querySelectorAll<HTMLElement>("[data-panel]")) {
    if (drafts.open.has(panel.dataset.panel ?? "")) {
      panel.classList.remove("hidden");
    }
  }
}
